import { Box, Typography } from '@mui/material';
import React from 'react';
import { useRoleLabels } from 'src/hooks/useRoleLabels';
import RelEntityHead from 'src/object-actions/components/RelEntityHead';
import RoleTypeIcon, { AssignmentRoleType } from 'src/theme/icons/RoleTypeIcon';
import { PaymentAssignments, RelEntity } from '../object-actions/types/types';
import { AssignmentRemoveRoleButton } from './AssignmentActions';

interface PaymentAssignmentCardProps {
    assignment: PaymentAssignments;
    extras?: React.ReactNode;
    /** When true, show placeholder rows for athlete, guardian, and coach roles that are not set. */
    showEmptyRoles?: boolean;
    /** When true, show inline remove buttons next to coach and guardian roles. Requires AssignmentRoleActionsProvider ancestor. */
    showRoleActions?: boolean;
}

const roleIconSx = { color: 'text.secondary', fontSize: '1.25rem', flexShrink: 0 };

interface RoleSlotProps {
    role: AssignmentRoleType;
    label: string;
    rel?: RelEntity;
    titleVariant?: 'h5' | 'body1';
    empty?: boolean;
    removeButton?: React.ReactNode;
}

const RoleSlot: React.FC<RoleSlotProps> = ({
    role,
    label,
    rel,
    titleVariant = 'body1',
    empty = false,
    removeButton,
}) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
        }}
    >
        <RoleTypeIcon role={role} sx={roleIconSx} />
        <Box sx={{ minWidth: 0 }}>
            {empty || !rel ? (
                <Typography variant={titleVariant === 'h5' ? 'h5' : 'body1'} color="text.secondary" component="div">
                    {label} not set
                </Typography>
            ) : (
                <RelEntityHead rel={rel} label={label} titleVariant={titleVariant} />
            )}
        </Box>
        {removeButton}
    </Box>
);

const PaymentAssignmentCard: React.FC<PaymentAssignmentCardProps> = ({
    assignment,
    extras,
    showEmptyRoles = false,
    showRoleActions = false,
}) => {
    const { applyRoleLabels } = useRoleLabels();
    const athleteLabel = applyRoleLabels('Athlete');
    const guardianLabel = applyRoleLabels('Guardian');
    const coachLabel = applyRoleLabels('Coach');

    const parents = assignment.parents ?? [];
    const coaches = assignment.coaches ?? [];
    const showSupportRoles = showEmptyRoles || parents.length > 0 || coaches.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.25 }}>
                        <RoleTypeIcon role="athlete" sx={roleIconSx} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {assignment.athlete ? (
                            <RelEntityHead
                                rel={assignment.athlete}
                                label={athleteLabel}
                                titleVariant="h5"
                            />
                        ) : (
                            <Typography variant="h5" component="h2" color="text.secondary">
                                {athleteLabel} not set
                            </Typography>
                        )}
                    </Box>
                </Box>
                {extras && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {extras}
                    </Box>
                )}
            </Box>

            {showSupportRoles && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {parents.length > 0 ? (
                        parents.map((parent) => (
                            <RoleSlot
                                key={parent.id}
                                role="parent"
                                label={guardianLabel}
                                rel={parent as RelEntity<'Users'>}
                                removeButton={
                                    showRoleActions ? (
                                        <AssignmentRemoveRoleButton
                                            type="parent"
                                            id={parent.id}
                                            name={parent.str}
                                        />
                                    ) : undefined
                                }
                            />
                        ))
                    ) : showEmptyRoles ? (
                        <RoleSlot role="parent" label={guardianLabel} empty />
                    ) : null}

                    {coaches.length > 0 ? (
                        coaches.map((coach) => (
                            <RoleSlot
                                key={coach.id}
                                role="coach"
                                label={coachLabel}
                                rel={coach as RelEntity<'Users'>}
                                removeButton={
                                    showRoleActions ? (
                                        <AssignmentRemoveRoleButton
                                            type="coach"
                                            id={coach.id}
                                            name={coach.str}
                                        />
                                    ) : undefined
                                }
                            />
                        ))
                    ) : showEmptyRoles ? (
                        <RoleSlot role="coach" label={coachLabel} empty />
                    ) : null}
                </Box>
            )}
        </Box>
    );
};

export default PaymentAssignmentCard;
